import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import { IconButton, Paper, Stack } from "@mui/material";
import { styled } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import { SavedNewsRow } from './Item';

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: 'inherit',
    padding: theme.spacing(1),
    textAlign: 'center',
    color: '#fff',
    boxShadow: 'none'
}));

const DATA_LIMIT = 30;

// Bespoke, lightweight pagination for Saved News. Modeled structurally on the
// slice-based approach shared by CustomPagination.js and its siblings
// (CustomPublisherPagination.js, CustomProfilPagination.js,
// CustomDiscussionPagination.js, CustomSpacePagination.js), but deliberately
// deviates from them:
//   - page index is LOCAL component state, not the shared global
//     state.page/pageActions Redux slice every sibling uses - avoids the
//     cross-tab "stranded on an empty page" bug (e.g. leaving All News on
//     page 5 then switching to Saved News would otherwise land on the same
//     page number there too).
//   - no own <h1> heading - SocialMediaListRender.js already renders the
//     "Saved News" heading above this component.
//   - none of the ~20 unrelated loading selectors the siblings copy-paste -
//     Saved News' loading/empty state is handled upstream, in
//     SocialMediaListRender.js's SAVED_NEWS_NR branch, before this component
//     is ever rendered (it only ever receives a non-empty `data`).
export const CustomSavedNewsPagination = ({ data, theme, pinCounts, onUnsave }) => {
    const { width } = useWindowDimensions();
    const [page, setPage] = useState(1);

    const pages = data.length % DATA_LIMIT === 0
        ? data.length / DATA_LIMIT
        : Math.floor(data.length / DATA_LIMIT) + 1;

    // Keep the local page index in range if the list shrinks out from under
    // it (e.g. unsaving the last item on the last page) - steps back rather
    // than rendering an empty page with stale Prev/Next state.
    useEffect(() => {
        if (page > pages) {
            setPage(Math.max(1, pages));
        }
    }, [pages, page]);

    const getPaginatedData = () => {
        const startIndex = page * DATA_LIMIT - DATA_LIMIT;
        const endIndex = startIndex + DATA_LIMIT;
        return data.slice(startIndex, endIndex);
    };

    const goToTop = () => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth",
        });
    }

    const goToNextPage = () => {
        if (page < pages) {
            setPage(page + 1);
            goToTop();
        }
    }

    const goToPreviousPage = () => {
        if (page > 1) {
            setPage(page - 1);
            goToTop();
        }
    }

    const paginatedData = getPaginatedData();

    return (
        <>
            {paginatedData.map((item, idx) => (
                <SavedNewsRow key={item.url || idx} item={item} theme={theme} pinCounts={pinCounts} onUnsave={onUnsave} />
            ))}

            {pages > 1 ?
                <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    spacing={0}
                    p={5}
                >
                    {page > 1 ? <Item>
                        <IconButton style={{ marginTop: '-5px', background: "#1976d2", color: "#fff" }} onClick={goToPreviousPage} title="Previous">
                            <ArrowCircleLeftIcon fontSize="large" />
                        </IconButton>
                    </Item> : null}
                    {page < pages ? <Item>
                        <IconButton style={{ marginTop: '-5px', background: "#1976d2", color: "#fff" }} onClick={goToNextPage} title="Next">
                            <ArrowCircleRightIcon fontSize="large" />
                        </IconButton>
                    </Item> : null}
                </Stack> : null}

            {paginatedData && paginatedData.length >= 1 ? <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={0}
                p={1}
                style={{ paddingBottom: 10, marginBottom: 15, paddingTop: pages === 1 ? 50 : 0 }}
            >
                <Item>
                    <IconButton style={{ marginTop: '-5px', background: "#1976d2", color: "#fff" }} onClick={() => goToTop()} title="Up">
                        <ArrowCircleUpIcon fontSize="large" />
                    </IconButton>
                </Item>
            </Stack> : null}

            <div style={{ paddingBottom: width < 500 ? 25 : 100, paddingTop: width < 500 ? 25 : 50, marginTop: 5, marginBottom: 70 }} />
        </>
    );
}
